import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { notification } from 'antd'
import { useTranslation } from 'react-i18next'
import { assignClientCampaign, deleteClient, fetchClients, inviteClients, unassignClientCampaign } from './api'
import { InviteClientInput } from './client'
import { onErrorNotifyAboutValidationError } from '../util'

export function useClients(companyId?: string) {
    return useQuery({
        queryFn: () => fetchClients(companyId!),
        queryKey: ['company', 'clients', companyId],
        enabled: !!companyId,
    })
}

export function useInviteClients(companyId?: string) {
    const queryClient = useQueryClient()
    const { t } = useTranslation()

    return useMutation({
        mutationFn: (input: InviteClientInput) => inviteClients(companyId!, input),
        onSuccess: () => {
            notification.success({ message: t('Invitation sent') })
            queryClient.invalidateQueries({ queryKey: ['company', 'clients', companyId] })
            queryClient.invalidateQueries({ queryKey: ['company', 'invitations', companyId] })
        },
        onError: onErrorNotifyAboutValidationError,
    })
}

export function useDeleteClient(companyId?: string, userId?: string) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: () => deleteClient(companyId!, userId!),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['company', 'clients', companyId] })
        },
    })
}

export function useAssignClientCampaign(companyId?: string, userId?: string) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (campaignId: string) => assignClientCampaign(companyId!, userId!, campaignId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['company', 'clients', companyId] })
        },
        onError: onErrorNotifyAboutValidationError,
    })
}

export function useUnassignClientCampaign(companyId?: string, userId?: string) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (campaignId: string) => unassignClientCampaign(companyId!, userId!, campaignId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['company', 'clients', companyId] })
        },
        onError: onErrorNotifyAboutValidationError,
    })
}
